import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import devKgService from 'services/devKg';
import { fetchKnowledgeGraphs } from 'store/modules/knowledgeGraph';
import { generateDefaultBranchName } from 'utils/branchUtils';
import { trackEvent } from 'utils/eventTracking';
import BranchSelect from '../BranchSelect/BranchSelect';
import BranchCreateModal from '../BranchCreateModal/BranchCreateModal';
import styles from './BranchSwitcher.scss';

const BranchSwitcher = ({ knowledgeGraphs, dispatch, user, meta, enqueueSnackbar }) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [showCreateBranchModal, setShowCreateBranchModal] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Only show for super users or higher
  const canManageBranches = user?.isSuperUser || user?.isAdmin;

  const currentKgId = meta?.id;
  const currentKg = useMemo(() => knowledgeGraphs?.find(kg => kg.id === currentKgId), [knowledgeGraphs, currentKgId]);

  // Determine if current branch is main or dev
  const isMainBranch = useMemo(() => {
    if (!currentKg) return false;
    return !currentKg.developmentKgForName; // Main branches don't have this property
  }, [currentKg]);

  const mainKgId = useMemo(
    () =>
      currentKg?.developmentKgForName
        ? knowledgeGraphs?.find(kg => kg.info.default.name === currentKg.developmentKgForName)?.id
        : currentKgId,
    [currentKg, knowledgeGraphs, currentKgId]
  );

  const branches = useMemo(() => {
    if (!mainKgId) return [];

    const mainKg = knowledgeGraphs?.find(kg => kg.id === mainKgId);
    const devBranches = knowledgeGraphs?.filter(kg => kg.developmentKgForName === mainKg?.info.default.name) || [];

    return [
      {
        id: mainKgId,
        name: t('branch.main'),
        isMain: true,
        isActive: currentKgId === mainKgId
      },
      ...devBranches.map(kg => ({
        id: kg.id,
        name: kg.info.default.name,
        isMain: false,
        isActive: currentKgId === kg.id
      }))
    ];
  }, [knowledgeGraphs, mainKgId, currentKgId, t]);

  const handleBranchSwitch = branchId => {
    if (branchId === 'create-new') {
      trackEvent('KG Branch Creation Modal Opened', {
        currentBranch: currentKgId,
        mainKgId,
        context: 'chat'
      });
      setShowCreateBranchModal(true);
      setNewBranchName(generateDefaultBranchName(user));
      setValidationError('');
      return;
    }

    if (branchId !== currentKgId) {
      const targetBranch = branches.find(branch => branch.id === branchId);
      trackEvent('KG Branch Switched', {
        fromBranch: currentKgId,
        toBranch: branchId,
        fromType: isMainBranch ? 'main' : 'dev',
        toType: targetBranch?.isMain ? 'main' : 'dev',
        mainKgId,
        context: 'chat'
      });

      const params = new URLSearchParams(location.search);
      params.set('id', branchId);
      const newLocation = location.pathname + '?' + params.toString() + location.hash;
      history.push(newLocation);
    }
  };

  const handleCreateBranch = async () => {
    // Validate branch name
    const trimmedName = newBranchName.trim();
    if (!trimmedName) {
      setValidationError(t('branch.branch-name-required'));
      return;
    }

    // Apply same length limit as KG names (28 characters)
    if (trimmedName.length > 28) {
      setValidationError(t('branch.branch-name-too-long'));
      return;
    }

    if (!mainKgId) return;

    setValidationError('');
    setIsCreating(true);
    try {
      const createResult = await devKgService.createDevKnowledgeGraph(mainKgId, trimmedName);

      // Check if the request actually succeeded (HTTP service returns errors as resolved promises)
      if (createResult && createResult.response && createResult.response.status >= 400) {
        throw new Error(createResult.response.data?.message || createResult.message || 'Failed to create branch');
      }

      await dispatch(fetchKnowledgeGraphs());

      trackEvent('KG Branch Created', {
        branchName: trimmedName,
        mainKgId,
        userType: user?.isSuperUser ? 'superuser' : user?.isAdmin ? 'admin' : 'user',
        context: 'chat'
      });

      setShowCreateBranchModal(false);
      setNewBranchName('');
      enqueueSnackbar(t('branch.branch-created-successfully'), {
        variant: 'success',
        autoHideDuration: 3000
      });

      // Auto-switch to the newly created branch (consistent with Studio behavior)
      if (createResult && createResult.data) {
        handleBranchSwitch(createResult.data);
      }
    } catch (error) {
      console.error('Failed to create branch:', error);

      trackEvent('KG Branch Creation Failed', {
        branchName: trimmedName,
        mainKgId,
        error: error?.response?.data?.message || error.message,
        userType: user?.isSuperUser ? 'superuser' : user?.isAdmin ? 'admin' : 'user',
        context: 'chat'
      });

      const errorMessage = error?.response?.data?.message || t('branch.failed-to-create-branch');
      setValidationError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        persist: true
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleModalClose = () => {
    setShowCreateBranchModal(false);
    setNewBranchName('');
    setValidationError('');
  };

  if (!canManageBranches) {
    return null;
  }

  if (!currentKg) {
    return (
      <div className={styles.branchSwitcher}>
        <div className={styles.branchHeader}>
          <BranchSelect
            currentKgId=""
            isMainBranch={isMainBranch}
            branches={[]}
            isCreating={false}
            onBranchSwitch={() => {}}
            disabled
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.branchSwitcher}>
      <div className={styles.branchHeader}>
        <BranchSelect
          currentKgId={currentKgId}
          isMainBranch={isMainBranch}
          branches={branches}
          isCreating={isCreating}
          onBranchSwitch={handleBranchSwitch}
        />
      </div>

      <BranchCreateModal
        open={showCreateBranchModal}
        onClose={handleModalClose}
        newBranchName={newBranchName}
        setNewBranchName={setNewBranchName}
        validationError={validationError}
        setValidationError={setValidationError}
        isCreating={isCreating}
        onCreateBranch={handleCreateBranch}
      />
    </div>
  );
};

export const mapStateToProps = state => ({
  knowledgeGraphs: state.knowledgeGraphs.data,
  user: state.user
});

export default connect(mapStateToProps)(withSnackbar(BranchSwitcher));
